/**
 * Correspondence Service
 *
 * Handles correspondence CRUD operations and sending
 * Extracted from workflow-data.service.ts for better organization
 */

import {
  apiPost,
  apiPut,
  isSuccessResponse,
  extractErrorMessage
} from '@/lib/utils/api-helpers';
import { BACKEND_ENDPOINTS } from '../api/endpoints';
import type {
  CorrespondenceDraft,
  BulkSendResults,
  BackendCorrespondence,
  SendResult
} from '../workflow-types';

/**
 * The distinct backend-supplied reasons behind failed rows in a bulk send
 * result — dedup'd, first-seen order, empty values dropped. The one thing
 * every bulk-send caller needs once `results.failed > 0`, so a per-row
 * rejection (e.g. a missing signatory signature) surfaces its real cause
 * instead of each caller inventing its own generic string (VALE-1461).
 */
export function collectSendFailureReasons(results: SendResult[]): string[] {
  return Array.from(
    new Set(
      results
        .filter((r) => !r.success)
        .map((r) => r.error)
        .filter((error): error is string => Boolean(error))
    )
  );
}

/**
 * Transform correspondence type for backend
 * Backend expects: notification, collection-request, follow-up, reply
 */
function transformTypeForBackend(type: string): string {
  // No transformation needed - backend expects these exact values
  return type;
}

/**
 * Create bulk correspondence drafts
 * Creates multiple draft correspondences in a single request
 */
export async function createBulkDrafts(
  matterId: number,
  type: 'notification' | 'collection-request' | 'follow-up',
  institutionIds: number[]
): Promise<CorrespondenceDraft[]> {
  try {
    // Import prefetchLetterhead
    const { prefetchLetterhead } = await import('./letterhead.service');

    // Prefetch letterhead in parallel with creating drafts
    // This ensures it's cached and ready when users open letter editors
    const letterheadPromise = prefetchLetterhead();

    const payload = {
      type: transformTypeForBackend(type),
      institutionIds
    };

    console.log(`[CorrespondenceService] Creating bulk ${type} drafts:`, {
      matterId,
      payload,
      endpoint: `/api/proxy${BACKEND_ENDPOINTS.bulkCorrespondences(matterId)}`
    });

    const response = await apiPost(
      `/api/proxy${BACKEND_ENDPOINTS.bulkCorrespondences(matterId)}`,
      payload
    );

    console.log(`[CorrespondenceService] Bulk drafts response:`, {
      success: isSuccessResponse(response),
      status: response.status,
      data: response.data
    });

    if (!isSuccessResponse(response)) {
      throw new Error(extractErrorMessage(response));
    }

    // Wait for letterhead prefetch to complete (but don't fail if it fails)
    const letterheadUrl = await letterheadPromise;
    if (letterheadUrl) {
      console.log(
        '[CorrespondenceService] Letterhead prefetched and ready for use:',
        letterheadUrl
      );
    }

    return response.data as CorrespondenceDraft[];
  } catch (error) {
    console.error('Failed to create bulk drafts:', error);
    throw error;
  }
}

/**
 * Create single correspondence draft
 * For individual correspondence creation
 */
export async function createSingleDraft(
  matterId: number,
  draft: CorrespondenceDraft
): Promise<BackendCorrespondence> {
  try {
    const transformedDraft = {
      ...draft,
      type: transformTypeForBackend(draft.type)
    };

    const response = await apiPost(
      `/api/proxy${BACKEND_ENDPOINTS.correspondences(matterId)}`,
      transformedDraft
    );

    if (!isSuccessResponse(response)) {
      throw new Error(extractErrorMessage(response));
    }

    return response.data as BackendCorrespondence;
  } catch (error) {
    console.error('Failed to create single draft:', error);
    throw error;
  }
}

/**
 * Update existing correspondence draft
 */
export async function updateDraft(
  matterId: number,
  correspondenceId: number,
  updates: Partial<CorrespondenceDraft>
): Promise<BackendCorrespondence> {
  try {
    const response = await apiPut(
      `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}`,
      updates
    );

    if (!isSuccessResponse(response)) {
      throw new Error(extractErrorMessage(response));
    }

    return response.data as BackendCorrespondence;
  } catch (error) {
    console.error('Failed to update draft:', error);
    throw error;
  }
}

/**
 * Update correspondence signatory
 */
export async function updateCorrespondenceSignatory(
  matterId: number,
  correspondenceId: number,
  signatoryUserId: number | null
): Promise<BackendCorrespondence> {
  try {
    const payload = {
      signatoryUser: signatoryUserId ? { id: signatoryUserId } : null
    };

    const response = await apiPut(
      `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}`,
      payload
    );

    if (!isSuccessResponse(response)) {
      throw new Error(extractErrorMessage(response));
    }

    return response.data as BackendCorrespondence;
  } catch (error) {
    console.error('Failed to update correspondence signatory:', error);
    throw error;
  }
}

/**
 * Send correspondences
 * Sends multiple correspondences by their IDs
 */
export async function sendCorrespondences(
  matterId: number,
  correspondenceIds: number[]
): Promise<BulkSendResults> {
  try {
    console.log(
      'Sending correspondences:',
      correspondenceIds,
      'for matter:',
      matterId
    );

    const results = await Promise.allSettled(
      correspondenceIds.map((id) => {
        console.log('Sending correspondence ID:', id);
        return apiPost(
          `/api/proxy${BACKEND_ENDPOINTS.sendCorrespondence(matterId, id)}`,
          {}
        );
      })
    );

    // Process results
    const sendResults = results.map((result, index) => {
      if (result.status === 'fulfilled' && isSuccessResponse(result.value)) {
        const data = result.value.data as { id?: number } | undefined;
        return {
          institutionId: correspondenceIds[index],
          institutionName: '',
          success: true,
          correspondenceId: data?.id
        };
      } else {
        let error: string;
        if (result.status === 'rejected') {
          const reason = result.reason as { message?: string } | undefined;
          error = reason?.message || 'Send failed';
        } else {
          error = extractErrorMessage(result.value);
        }

        return {
          institutionId: correspondenceIds[index],
          institutionName: '',
          success: false,
          error
        };
      }
    });

    const successCount = sendResults.filter((r) => r.success).length;

    return {
      total: sendResults.length,
      sent: successCount,
      failed: sendResults.length - successCount,
      results: sendResults
    };
  } catch (error) {
    console.error('Failed to send correspondences:', error);
    throw error;
  }
}

/**
 * Get preview URL for a letter
 */
export function getLetterPreviewUrl(
  matterId: number,
  correspondenceId: number
): string {
  return `/api/proxy${BACKEND_ENDPOINTS.previewLetter(matterId, correspondenceId)}`;
}

/**
 * Get download URL for correspondence print package
 */
export function getCollectionDocumentsUrl(
  matterId: number,
  correspondenceId: number
): string {
  return `/api/proxy${BACKEND_ENDPOINTS.printPackage(matterId, correspondenceId)}`;
}
